import { createSelector } from '@ngrx/store';
import { createDeepEqualSelector } from 'NewAngular/state/selectors';
import { SCATTER_PLOT_STATE_KEYS } from './constants';
import { ScatterPlotPresentationOptionValue, ScatterPlotSeriesType, } from 'ChartApp/ts/enums/scatter-plot-common.enum';
import { formFlattenedObject } from 'NewAngular/utils/urls';
const getChartId = (store) => {
    return store.scatterPlot.chartId;
};
const getChartType = (store) => {
    return store.scatterPlot.chartType;
};
const getAxisExtremes = (store) => {
    return store.scatterPlot.axisExtremes;
};
const getDisplayAllCurrencyCodes = (store) => {
    return store.scatterPlot.displayAllCurrencyCodes;
};
const getDisplayAllDates = (store) => {
    return store.scatterPlot.displayAllDates;
};
const getDisplayAllNames = (store) => {
    return store.scatterPlot.displayAllNames;
};
const getDisplayAllXYData = (store) => {
    return store.scatterPlot.displayAllXYData;
};
const getDisplayAllTickers = (store) => {
    return store.scatterPlot.displayAllTickers;
};
const getDisplayBestFitLine = (store) => {
    return store.scatterPlot.displayBestFitLine;
};
const getDisplayLegend = (store) => {
    return store.scatterPlot.displayLegend;
};
const getDisplayLegendOnChart = (store) => {
    return store.scatterPlot.displayLegendOnChart;
};
const getPerformanceDisclosure = (store) => {
    return store.scatterPlot.performanceDisclosure;
};
const getNameInLegend = (store) => {
    return store.scatterPlot.nameInLegend;
};
const getTitle = (store) => {
    return store.scatterPlot[ScatterPlotPresentationOptionValue.title];
};
const getNote = (store) => {
    return store.scatterPlot[ScatterPlotPresentationOptionValue.note];
};
const getSize = (store) => {
    return store.scatterPlot[ScatterPlotPresentationOptionValue.size];
};
const getDisplayCurrencyCodeInLegend = (store) => {
    return store.scatterPlot.displayCurrencyCodeInLegend;
};
const getDisplayXYDataInLegend = (store) => {
    return store.scatterPlot.displayXYDataInLegend;
};
const getLabelPlacements = (store) => {
    return store.scatterPlot.labelPlacements;
};
const getPlotOptions = (store) => {
    return store.scatterPlot.plotOptions;
};
const getPlotType = (store) => {
    return store.scatterPlot.plotType;
};
const getSecurities = (store) => {
    return store.scatterPlot.securities;
};
const getDisplayColorRange = (store) => {
    return store.scatterPlot.displayColorRange;
};
const getUseCustomColors = (store) => {
    return store.scatterPlot.useCustomColors;
};
const getXAxisMetric = (store) => {
    return store.scatterPlot.xAxisMetric;
};
const getYAxisMetric = (store) => {
    return store.scatterPlot.yAxisMetric;
};
// Point in Time
export const getXAxisDate = (store) => {
    return store.scatterPlot.xAxisDate;
};
export const getYAxisDate = (store) => {
    return store.scatterPlot.yAxisDate;
};
export const getLookback = (store) => {
    return store.scatterPlot.lookback ?? null;
};
// Timeseries
export const getDataFormat = (store) => {
    return store.scatterPlot.dataFormat;
};
export const getStartDate = (store) => {
    return store.scatterPlot.startDate;
};
export const getEndDate = (store) => {
    return store.scatterPlot.endDate;
};
export const getChartOptions = (store) => {
    // Get chart options from store.scatterPlot slice
    const { displayAllCurrencyCodes, displayAllDates, displayAllNames, displayAllXYData, displayAllTickers, displayBestFitLine, displayLegend, displayCurrencyCodeInLegend, displayXYDataInLegend, displayColorRange, displayLegendOnChart, nameInLegend, useCustomColors, performanceDisclosure, } = store.scatterPlot;
    // Return an object of current chart options
    return {
        displayAllTickers,
        displayAllXYData,
        displayAllCurrencyCodes,
        displayAllDates,
        displayAllNames,
        displayBestFitLine,
        displayLegend,
        displayCurrencyCodeInLegend,
        displayXYDataInLegend,
        displayColorRange,
        displayLegendOnChart,
        nameInLegend,
        useCustomColors,
        performanceDisclosure,
    };
};
const getScatterPlotStore = createDeepEqualSelector(getChartId, getChartType, getAxisExtremes, 
// Interactive Chart Options
getDisplayAllCurrencyCodes, getDisplayAllDates, getDisplayAllNames, getDisplayAllXYData, getDisplayAllTickers, getDisplayBestFitLine, getDisplayLegend, getDisplayCurrencyCodeInLegend, getDisplayXYDataInLegend, getNameInLegend, 
// Presentation Chart Options
getDisplayLegendOnChart, getPerformanceDisclosure, 
// Presentation Options
getTitle, getNote, getSize, 
// Other
getLabelPlacements, getPlotOptions, getPlotType, getSecurities, 
// Color
getDisplayColorRange, getUseCustomColors, 
// Metrics
getXAxisMetric, getYAxisMetric, 
// Point in Time
getXAxisDate, getYAxisDate, getLookback, 
// Timeseries
getDataFormat, getEndDate, getStartDate, (chartId, chartType, axisExtremes, 
// Interactive Chart Options
displayAllCurrencyCodes, displayAllDates, displayAllNames, displayAllXYData, displayAllTickers, displayBestFitLine, displayLegend, displayCurrencyCodeInLegend, displayXYDataInLegend, nameInLegend, 
// Presentation Chart Options
displayLegendOnChart, performanceDisclosure, 
// Presentation Options
title, note, size, 
// Other
labelPlacements, plotOptions, plotType, securities, 
// Color
displayColorRange, useCustomColors, 
// Metrics
xAxisMetric, yAxisMetric, 
// Point in Time
xAxisDate, yAxisDate, lookback, 
// Timeseries
dataFormat, endDate, startDate) => {
    return {
        chartId,
        chartType,
        axisExtremes,
        // Interactive Chart options
        displayAllCurrencyCodes,
        displayAllDates,
        displayAllNames,
        displayAllXYData,
        displayAllTickers,
        displayBestFitLine,
        displayLegend,
        displayCurrencyCodeInLegend,
        displayXYDataInLegend,
        nameInLegend,
        // Presentation Chart Options
        displayLegendOnChart,
        performanceDisclosure,
        // Presentation Options
        title,
        note,
        size,
        // Other
        labelPlacements,
        plotOptions,
        plotType,
        securities,
        // Color
        displayColorRange,
        useCustomColors,
        // Metrics
        xAxisMetric,
        yAxisMetric,
        // Point in Time
        xAxisDate,
        yAxisDate,
        lookback,
        // Timeseries
        dataFormat,
        endDate,
        startDate,
    };
});
const getScatterPlotFetchParams = createSelector(getScatterPlotStore, (scatterPlotStore) => {
    return formFlattenedObject(scatterPlotStore, SCATTER_PLOT_STATE_KEYS);
});
// Securities Info
const getSecuritiesInfo = (store) => {
    return store.securitiesInfo;
};
const partitionSecuritiesAndLists = createSelector(getSecurities, (securitiesAndLists) => {
    const securities = [];
    const securityLists = [];
    securitiesAndLists.forEach((securityOrList) => {
        if (securityOrList.type === ScatterPlotSeriesType.security)
            securities.push(securityOrList);
        else
            securityLists.push(securityOrList);
    });
    return { securities, securityLists };
});
const hasSecurities = createSelector(partitionSecuritiesAndLists, (securitiesAndLists) => {
    const { securities } = securitiesAndLists;
    return securities?.length !== 0;
});
const hasEmptySecuritiesInfo = createSelector(getSecuritiesInfo, (securitiesInfo) => Object.keys(securitiesInfo).length === 0);
const loadingSecurities = createSelector(hasSecurities, hasEmptySecuritiesInfo, (_hasSecurities, _hasEmptySecuritiesInfo) => {
    return _hasSecurities && _hasEmptySecuritiesInfo;
});
const hasValidRequestData = createSelector(getXAxisMetric, getYAxisMetric, partitionSecuritiesAndLists, (xAxisMetric, yAxisMetric, securitiesAndLists) => {
    const { securities, securityLists } = securitiesAndLists;
    const includesSecurities = !!securities.find((security) => security.include);
    const includesLists = !!securityLists.find((securityList) => securityList.include);
    return !!(xAxisMetric && yAxisMetric && (includesSecurities || includesLists));
});
export { getChartId, getChartType, getLabelPlacements, getPlotOptions, getScatterPlotFetchParams, getXAxisMetric, getYAxisMetric, hasValidRequestData, loadingSecurities, partitionSecuritiesAndLists, };
